import { Box, styled, Button } from '@mui/material'
import {
    useAllApplications,
    useCheckAccessToCheckInOperation,
    useComputerCheckIn,
    useCurrentPerson,
} from 'hooks'
import moment from 'moment'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ComputerSessionRequestDetails from '../SessionRequestDetail'
import appConfig from 'config'
import MasterLockOverlay from 'packages/eid-ui/MasterLockOverlay'
import { useAppState } from 'appContext'
import { PasswordModal } from 'components/PasswordModal'
import { Avatar } from 'packages/eid-ui'
import { getFullImageUrl } from 'utils'
import ComputerConnectBtn from 'components/DynamicComponents/ComputerConnectButton'
import ComputersCheckInButton from 'components/DynamicComponents/ComputersCheckInButton'
import { useIsSmallScreen, useUrlGenerator } from 'packages/core'
import classNames from 'classnames'
import { textTransform } from '@mui/system'
const smallScreenWidth = '1022px'

interface PaddedDivProps {
    isSmallScreen: boolean
}

export const PaddedDiv = styled(Box)<PaddedDivProps>(({ isSmallScreen }) => ({
    padding: isSmallScreen ? '0 16px' : '0 31px',
}))

const RequestMainContainer = styled(Box)(({ theme }) => ({
    borderRadius: '0.8rem',
    boxShadow: '0 0.4rem 1.6rem 0 rgba(0, 0, 0, 0.05)',
    border: 'solid 0.1rem rgba(0, 0, 0, 0.11)',
    background: theme?.palette?.background?.paper,
    marginTop: '1.7rem',
    paddingBottom: '2rem',
    overflow: 'hidden',
}))

const RequestHeaderTitle = styled(Box)({
    fontSize: '1.4rem',
    fontWeight: 600,
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
})

const RequestHeader = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: 'solid 0.1rem rgba(0, 0, 0, 0.11)',
    padding: '0.4rem 2rem',
    backgroundImage:
        'linear-gradient(0deg, rgb(0, 0, 0, 0.02) 12.5%, rgb(255, 255, 255) 12.5%, rgb(255, 255, 255) 50%, rgb(0, 0, 0, 0.02) 50%, rgb(0, 0, 0, 0.02) 62.5%, rgb(255, 255, 255) 62.5%, rgb(255, 255, 255) 100%)',
    backgroundSize: '0.8rem 0.8rem',
})

const RequestHeaderTitleButton = styled(Box)(({ theme }) => ({
    '& button': {
        fontSize: '1.1rem',
        color: theme?.palette?.common?.white,
        background: theme?.palette?.secondary?.main,
        borderRadius: '0.5rem',
        padding: '0.1rem 0.4rem 0.1rem',
        textTransform: 'capitalize',
        '&:hover': {
            background: theme?.palette?.secondary?.main,
        },
    },
}))

const RequestItemShow = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 2rem',
    marginTop: '0.7rem',
    [`@media (max-width:${smallScreenWidth})`]: {
        flexDirection: 'column',
        alignItems: 'start',
        gap: '1.6rem',
    },
})

const RequestLeftShow = styled(Box)({
    // TODO: Commenting until we get new design
    // width: '25%',
})

const RequestItemTitle = styled(Box)({
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: '#9b9b9b',
    margin: '0rem',
    textTransform: 'uppercase',
    [`@media (max-width:${smallScreenWidth})`]: {
        fontWeight: '600',
    },
})

const RequestItemHeading = styled(Box)({
    fontSize: '1.4rem',
    fontWeight: 500,
    margin: '0rem',
})

const RequestItemHeadingLink = styled(RequestItemHeading)(({ theme }) => ({
    '& a': {
        color: `${theme?.palette?.primary?.main} !important`,
        textDecoration: 'none !important',
    },
}))

const RequestItemTime = styled(Box)(({ theme }) => ({
    fontSize: '1.4rem',
    fontWeight: 500,
    color: theme?.palette?.common?.black,
    margin: '0rem',
    '& button': {
        fontSize: '1.1rem',
        borderRadius: '0.5rem',
        padding: '0.1rem 0.4rem 0.1rem',
        textTransform: 'capitalize',
        '&:hover': {
            background: theme?.palette?.secondary?.main,
        },
    },
}))

const CheckInButton = styled('button')(({ theme }) => ({
    fontSize: '1.1rem',
    color: theme?.palette?.common?.white,
    background: theme?.palette?.primary?.main,
    borderRadius: '0.5rem',
    '&:hover': {
        background: theme?.palette?.primary?.main,
    },
}))

const AvtarSection = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    ' & > div': {
        height: '2rem',
        width: '2rem',
    },
})

const AvtarName = styled('span')({
    marginLeft: '0.5rem',
})

const RequestConnectBtn = styled(Box)(({ theme }) => ({
    ' & button': {
        '& :hover': {
            background: `${theme?.palette?.primary?.main} !important`,
        },
        '& p': {
            padding: '0 10px',
        },
      
        ' & div': {
            maxWidth: 'auto !important',
        },
    },
}))

export interface ComputerSessionRequestTileProps {
    item: any
    parent: any
}

export const ComputerSessionRequestTile: FC<ComputerSessionRequestTileProps> = (
    props,
) => {
    const { item, parent } = props
    item.checkOutId = item.id
    const [{ masterPassword }]: any = useAppState()
    const [open, setOpen] = useState('')
    const { t } = useTranslation()
    const [showDetailsDialog, setShowDetalsDialog] = useState(false)
    const { getMyTasksUrl } = useUrlGenerator(useAllApplications)
    const isSmallScreen = useIsSmallScreen()

    const { data: currentPerson } = useCurrentPerson()
    const handleDetailClick = () => {
        setShowDetalsDialog(true)
    }
    const isRequestedBySelf = item.isSelfRequested

    const { data: hasAccessToCheckIn } = useCheckAccessToCheckInOperation(
        item.externalCredentialID,
        isRequestedBySelf === false && item.showCheckIn,
    )
    const {
        mutateAsync: checkInComputer,
        data: computerChkIn,
        isError,
    } = useComputerCheckIn()

    const handleCheckIn = () => {
        checkInComputer(parent.checkOutId)
    }

    useEffect(() => {
        if (computerChkIn && !isError) {
            setShowDetalsDialog(false)
        }
    }, [computerChkIn, isError])

    const handleConnect = () => {
        const wfParams = []
        wfParams.push(`TargetPersonGuid=${currentPerson.id}`)
        wfParams.push(`AccessRequestPolicyID=${item.requestPolicyId}`)
        wfParams.push(`TargetComputerGuid=${item.id}`)
        wfParams.push(`IsAdvanceMode=${false}`)
        wfParams.push(`CloseWindowAfter=0`)
        const fullWorkflowUrl = constructApprovalWorkflowUrl(
            appConfig.BASE_EID_URL,
            'OpenPSMSessionWF',
            wfParams,
        )
        window.open(fullWorkflowUrl, '_blank', 'noopener,noreferrer')
    }

    const handleMasterUnlock = () => {
        setOpen('enterPassword')
    }
    return (
        <>
            {!masterPassword && (
                <PasswordModal
                    showDefaultContent={false}
                    open={open}
                    setOpen={setOpen}
                />
            )}
            <PaddedDiv isSmallScreen={isSmallScreen}>
                <Box
                    sx={{
                        borderImageSource:
                            'linear-gradient(to right, rgba(1, 174, 143, 0), #04ab936e 7%, #2c83bd47 91%, rgba(48, 127, 193, 0))',
                        borderImageSlice: '1',
                        borderBottomWidth: '1px',
                        borderStyle: 'solid',
                        borderTopWidth: '0',
                        overflow: 'hidden',
                    }}
                >
                    <RequestMainContainer>
                        <RequestHeader>
                            <RequestHeaderTitle as="h2">
                                {item.credentialFriendlyName
                                    ? item.credentialFriendlyName
                                    : '---'}
                            </RequestHeaderTitle>
                            <RequestHeaderTitleButton>
                                <Button onClick={() => handleDetailClick()}>
                                    {t('ManageAccess_Details')}
                                </Button>
                            </RequestHeaderTitleButton>
                        </RequestHeader>
                        <RequestItemShow>
                            <RequestLeftShow>
                                <RequestItemTitle as="h2">
                                    {t('Common_BusinessRequest')}
                                </RequestItemTitle>
                                {item?.businessRequestId &&
                                item.businessRequestName ? (
                                    <RequestItemHeadingLink as="h2">
                                        <a
                                            rel="noreferrer"
                                            target="_blank"
                                            href={getMyTasksUrl(
                                                `/myRequests/businessRequests?id=${item.businessRequestId}`,
                                            )}
                                        >
                                            {item.businessRequestName
                                                ? item.businessRequestName
                                                : '---'}
                                        </a>
                                    </RequestItemHeadingLink>
                                ) : (
                                    <RequestItemHeading as="h2">
                                        {item.businessRequestName
                                            ? item.businessRequestName
                                            : '---'}
                                    </RequestItemHeading>
                                )}
                            </RequestLeftShow>
                            <RequestLeftShow>
                                <RequestItemTitle as="h2">
                                    {t('Common_BusinessRequestItem')}
                                </RequestItemTitle>
                                {item?.businessRequestItemId &&
                                item.businessRequestItemName ? (
                                    <RequestItemHeadingLink as="h2">
                                        <a
                                            rel="noreferrer"
                                            target="_blank"
                                            href={getMyTasksUrl(
                                                `/myRequests/businessRequestItems?id=${item.businessRequestItemId}`,
                                            )}
                                        >
                                            {item.businessRequestItemName
                                                ? item.businessRequestItemName
                                                : '---'}
                                        </a>
                                    </RequestItemHeadingLink>
                                ) : (
                                    <RequestItemHeading as="h2">
                                        {item.businessRequestItemName
                                            ? item.businessRequestItemName
                                            : '---'}
                                    </RequestItemHeading>
                                )}
                            </RequestLeftShow>
                            <RequestLeftShow>
                                <RequestItemTitle as="h2">
                                    {t('CheckedOutBy')}
                                </RequestItemTitle>
                                <AvtarSection>
                                    <Avatar
                                        size="small"
                                        src={getFullImageUrl(
                                            item.requestedByPersonImageThumbUrl,
                                        )}
                                    />
                                    <AvtarName>
                                        {item.requestorFriendlyName
                                            ? item.requestorFriendlyName
                                            : '---'}
                                    </AvtarName>
                                </AvtarSection>
                            </RequestLeftShow>
                        </RequestItemShow>

                        <RequestItemShow>
                            <RequestLeftShow>
                                <RequestItemTitle as="h2">
                                    {t('Common_StartTime')}
                                </RequestItemTitle>
                                <RequestItemTime as="h2">
                                    {item.startTimeUtc
                                        ? moment
                                              .utc(item.startTimeUtc)
                                              .local()
                                              .format('L LT')
                                        : '---'}
                                </RequestItemTime>
                            </RequestLeftShow>
                            <RequestLeftShow>
                                <RequestItemTitle as="h2">
                                    {t('Common_EndTime')}
                                </RequestItemTitle>
                                <RequestItemTime as="h2">
                                    {item.endTimeUtc
                                        ? moment
                                              .utc(item.endTimeUtc)
                                              .local()
                                              .format('L LT')
                                        : '---'}
                                </RequestItemTime>
                            </RequestLeftShow>
                            {item.showFutureCheckOutTime ? (
                                <Box>
                                    <RequestItemTitle as="h2">
                                        {t('Common_ActualCheckOutTime')}
                                    </RequestItemTitle>
                                    <RequestItemTime as="h2">
                                        {t('Common_WillBeAvailableForCheckOut')}
                                        {item.userCheckedOutTimeUtc
                                            ? moment
                                                  .utc(
                                                      item.userCheckedOutTimeUtc,
                                                  )
                                                  .local()
                                                  .format('L LT')
                                            : '---'}
                                    </RequestItemTime>
                                </Box>
                            ) : (
                                <Box>
                                    <RequestItemTitle as="h2">
                                        {t('Common_ActualCheckOutTime')}
                                    </RequestItemTitle>
                                    <RequestItemTime as="h2">
                                        {item.userCheckedOutTimeUtc
                                            ? moment
                                                  .utc(
                                                      item.userCheckedOutTimeUtc,
                                                  )
                                                  .local()
                                                  .format('L LT')
                                            : '---'}
                                    </RequestItemTime>
                                </Box>
                            )}
                            {item.showActualCheckInTime && (
                                <RequestLeftShow>
                                    <RequestItemTitle as="h2">
                                        {t('Common_ActualCheckInTime')}
                                    </RequestItemTitle>
                                    <RequestItemTime as="h2">
                                        {item.userCheckedInTimeUtc
                                            ? moment
                                                  .utc(
                                                      item.userCheckedInTimeUtc,
                                                  )
                                                  .local()
                                                  .format('L LT')
                                            : '---'}
                                    </RequestItemTime>
                                </RequestLeftShow>
                            )}
                            {item.showStatus && (
                                <RequestLeftShow>
                                    <RequestItemTitle as="h2">
                                        {t('Status')}
                                    </RequestItemTitle>
                                    <RequestItemTime as="h2">
                                        <MasterLockOverlay
                                            btnLabel={t('Common_Unlock')}
                                            showMasterLock={!masterPassword}
                                            handleMasterUnlock={
                                                handleMasterUnlock
                                            }
                                        >
                                            <RequestConnectBtn>
                                                <ComputerConnectBtn
                                                    parentStyle=""
                                                    showPrefixIcon={false}
                                                    data={item}
                                                    drawerView={false}
                                                    showCalenderIcon={false}
                                                />
                                            </RequestConnectBtn>
                                        </MasterLockOverlay>
                                    </RequestItemTime>
                                </RequestLeftShow>
                            )}
                            {item.showCheckIn &&
                                hasAccessToCheckIn?.hasAccess && (
                                    <RequestLeftShow>
                                        <RequestItemTitle as="h2">
                                            {t('Status')}
                                        </RequestItemTitle>
                                        <RequestItemTime as="h2">
                                            <MasterLockOverlay
                                                btnLabel={t('Common_Unlock')}
                                                showMasterLock={!masterPassword}
                                                handleMasterUnlock={
                                                    handleMasterUnlock
                                                }
                                            >
                                                <RequestConnectBtn>
                                                    <ComputersCheckInButton
                                                        data={{
                                                            resource: item,
                                                        }}
                                                    />
                                                </RequestConnectBtn>
                                            </MasterLockOverlay>
                                        </RequestItemTime>
                                    </RequestLeftShow>
                                )}
                        </RequestItemShow>
                    </RequestMainContainer>
                </Box>
            </PaddedDiv>
            <ComputerSessionRequestDetails
                parent={parent}
                masterPassword={masterPassword}
                handleMasterUnlock={handleMasterUnlock}
                handleConnect={() => handleConnect()}
                handleCheckIn={handleCheckIn}
                canCheckIn={item.canCheckIn && hasAccessToCheckIn?.hasAccess}
                showStatus={item.showStatus}
                item={item}
                open={showDetailsDialog}
                onClose={() => setShowDetalsDialog(false)}
                title={
                    item.credentialFriendlyName
                        ? `${item.credentialFriendlyName} ${
                              item.userCheckedInTimeUtc
                                  ? t('Common_StartingAt') +
                                    ' ' +
                                    moment
                                        .utc(item.userCheckedInTimeUtc)
                                        .local()
                                        .format('L LT')
                                  : ''
                          }`
                        : '---'
                }
            />
        </>
    )
}

const constructApprovalWorkflowUrl = (
    baseEidUrl: string,
    workflowName: string,
    workflowParams: string[],
) => {
    let queryParams = ['popupNav=true']

    if (workflowParams) {
        queryParams = queryParams.concat(workflowParams)
    }

    const queryString = queryParams.join('&')
    return `${baseEidUrl}/ui?hideHeader=true&wfMsContext=true&wfMsUrl=${window.location.origin}#w/${workflowName}?${queryString}`
}

export default ComputerSessionRequestTile
